import { createContext, useCallback, useContext, useRef, useState } from 'react';
import type { ReactNode } from 'react';
import { AnimatePresence, motion } from 'motion/react';
import { CheckCircle2, AlertCircle } from 'lucide-react';
import { cn } from '@/lib/cn';
import { PulseLoader } from './pulse-loader';

type ToastTone = 'default' | 'positive' | 'critical' | 'loading';
interface Toast {
  id: number;
  message: ReactNode;
  tone: ToastTone;
}

interface ToastCtx {
  /** Shows a toast, returns its id. `loading` toasts stay up until you
   * `dismiss()` or `update()` them (with a long safety-net timeout). */
  toast: (message: ReactNode, tone?: ToastTone) => number;
  /** Swap a still-visible toast's message/tone in place — e.g. flip a
   * `loading` toast into `positive` once the request resolves. */
  update: (id: number, message: ReactNode, tone?: ToastTone) => void;
  dismiss: (id: number) => void;
}

const Ctx = createContext<ToastCtx | null>(null);

// eslint-disable-next-line react-refresh/only-export-components
export function useToast(): ToastCtx {
  const ctx = useContext(Ctx);
  if (!ctx) throw new Error('useToast must be used within <ToastProvider>');
  return ctx;
}

// Only this many stack up at once — a burst of actions replaces the oldest
// instead of piling on indefinitely.
const MAX_VISIBLE = 3;

let seq = 0;

export function ToastProvider({ children }: { children: ReactNode }) {
  const [toasts, setToasts] = useState<Toast[]>([]);
  const timers = useRef(new Map<number, ReturnType<typeof setTimeout>>());

  const clearTimer = useCallback((id: number) => {
    const t = timers.current.get(id);
    if (t) {
      clearTimeout(t);
      timers.current.delete(id);
    }
  }, []);

  const dismiss = useCallback(
    (id: number) => {
      clearTimer(id);
      setToasts((t) => t.filter((x) => x.id !== id));
    },
    [clearTimer],
  );

  const arm = useCallback(
    (id: number, tone: ToastTone) => {
      clearTimer(id);
      timers.current.set(
        id,
        setTimeout(() => dismiss(id), tone === 'loading' ? 15_000 : 4000),
      );
    },
    [clearTimer, dismiss],
  );

  const toast = useCallback(
    (message: ReactNode, tone: ToastTone = 'default') => {
      const id = ++seq;
      setToasts((t) => {
        const next = [...t, { id, message, tone }];
        // Drop the oldest once we're over the cap — clear its timer too.
        while (next.length > MAX_VISIBLE) {
          const gone = next.shift();
          if (gone) clearTimer(gone.id);
        }
        return next;
      });
      arm(id, tone);
      return id;
    },
    [arm, clearTimer],
  );

  const update = useCallback(
    (id: number, message: ReactNode, tone: ToastTone = 'default') => {
      setToasts((t) => t.map((x) => (x.id === id ? { ...x, message, tone } : x)));
      arm(id, tone);
    },
    [arm],
  );

  return (
    <Ctx.Provider value={{ toast, update, dismiss }}>
      {children}
      <div className="pointer-events-none fixed inset-x-0 bottom-4 z-[100] flex flex-col-reverse items-center gap-2 px-4">
        <AnimatePresence initial={false}>
          {toasts.map((t, i) => {
            const depthFromTop = toasts.length - 1 - i; // 0 = newest
            return (
              <motion.div
                key={t.id}
                layout
                initial={{ opacity: 0, y: 24, scale: 0.9 }}
                animate={{ opacity: 1, y: 0, scale: 1 - depthFromTop * 0.04 }}
                exit={{ opacity: 0, scale: 0.9, y: -6 }}
                transition={{ type: 'spring', stiffness: 420, damping: 32 }}
                className={cn(
                  'pointer-events-auto flex max-w-sm items-center gap-2 rounded-pill border-2 border-ink px-4 py-2 text-sm font-semibold shadow-hard',
                  t.tone === 'positive' && 'bg-flavor-melon-soft text-flavor-melon-ink',
                  t.tone === 'critical' && 'bg-flavor-berry-soft text-flavor-berry-ink',
                  t.tone === 'loading' && 'bg-flavor-lychee-soft text-flavor-lychee-ink',
                  t.tone === 'default' && 'bg-surface text-ink',
                )}
                role="status"
              >
                {t.tone === 'positive' && <CheckCircle2 className="size-4 shrink-0" />}
                {t.tone === 'critical' && <AlertCircle className="size-4 shrink-0" />}
                {t.tone === 'loading' && <PulseLoader flavor="lychee" size="sm" />}
                <span>{t.message}</span>
              </motion.div>
            );
          })}
        </AnimatePresence>
      </div>
    </Ctx.Provider>
  );
}
